import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormLabel,
  FormControl,
  Text,
  useToast
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'

interface ICoursesObjectivesProps {
  isOpen: boolean
  onClose: () => void
  onFinished: () => void
  initialData?: { id: number; name: string } | null
  service: {
    create: (data: { name: string }) => Promise<ICourseInterface>
    update: (id: number, data: { name: string }) => Promise<ICourseInterface>
  }
}

export function CoursesObjectivesModal({
  isOpen,
  onClose,
  onFinished,
  initialData = null,
  service
}: ICoursesObjectivesProps) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const isEditMode = !!initialData

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name || '')
      setError('')
    }
  }, [isOpen, initialData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      setError('O nome é obrigatório.')
      return
    }

    setSaving(true)
    setError('')

    try {
      if (isEditMode && initialData) {
        await service.update(initialData.id, { name: name.trim() })
        toast({
          title: 'Objetivo atualizado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
      } else {
        await service.create({ name: name.trim() })
        toast({
          title: 'Objetivo criado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true
        })
      }
      onFinished()
      handleClose()
    } catch (err: unknown) {
      const error = err as AxiosError<{ message: string; errors?: Record<string, string[]> }>

      if (error.response?.status === 422 && error.response.data.errors?.name?.[0]) {
        toast({
          title: 'Erro ao salvar objetivo',
          description: error.response.data.errors.name[0],
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      } else {
        toast({
          title: 'Erro ao salvar objetivo',
          description: 'Tente novamente.',
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setName('')
    setError('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isEditMode ? 'Editar Objetivo do Curso' : 'Adicionar Novo Objetivo'}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <FormControl isInvalid={!!error}>
              <FormLabel>Nome do objetivo</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Digite o nome do objetivo"
                isDisabled={saving}
                autoFocus
              />
              {error && (
                <Text color="red.500" mt={1} fontSize="sm">
                  {error}
                </Text>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={saving}>
              Cancelar
            </Button>
            <Button colorScheme={isEditMode ? 'blue' : 'magenta'} onClick={handleSubmit} isLoading={saving}>
              Salvar
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
